import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { FilePreviewService } from './file-preview.service';
import { FileKind } from './file-preview.types';

@Component({
  selector: 'app-file-preview-dialog',
  templateUrl: './file-preview-dialog.component.html',
  styleUrls: ['./file-preview-dialog.component.scss']
})
export class FilePreviewDialogComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() fileUrl: string | null = null;
  @Input() fileBlob: File | Blob | null = null;
  @Input() fileName: string | null = null;
  /**
   * Ofrece «Descargar» también con `fileBlob`: emite `downloadRequested` con el nombre del archivo y quien abrió el
   * diálogo lo guarda. Sin él, «Descargar» solo sale con `fileUrl`, como en los demás usos.
   */
  @Input() downloadable = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() downloadRequested = new EventEmitter<string>();

  blob: Blob | null = null;
  kind: FileKind | null = null;
  resolvedName: string | null = null;
  loading = false;
  error: string | null = null;

  private fetchSub: Subscription | null = null;
  private lastResolvedKey: string | null = null;

  constructor(private previewService: FilePreviewService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      if (this.visible) {
        this.resolve();
      } else {
        this.cleanupTransient();
      }
    } else if (this.visible && (changes['fileUrl'] || changes['fileBlob'] || changes['fileName'])) {
      this.resolve();
    }
  }

  ngOnDestroy(): void {
    this.fetchSub?.unsubscribe();
    this.cleanupTransient();
  }

  onHide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.cleanupTransient();
  }

  get canDownload(): boolean {
    return !!this.fileUrl || (this.downloadable && !!this.fileBlob);
  }

  onDownloadClick(): void {
    if (this.fileUrl) {
      this.downloadRequested.emit(this.fileUrl);
    } else if (this.downloadable && this.fileBlob) {
      this.downloadRequested.emit(this.resolvedName ?? '');
    }
  }

  formatFileSize(bytes: number | undefined | null): string {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  get fileSize(): number | null {
    return this.blob?.size ?? (this.fileBlob instanceof File ? this.fileBlob.size : null);
  }

  private resolve(): void {
    this.fetchSub?.unsubscribe();
    this.error = null;

    if (this.fileBlob) {
      const name = this.fileName ?? (this.fileBlob instanceof File ? this.fileBlob.name : 'archivo');
      this.resolvedName = name;
      this.blob = this.fileBlob;
      this.kind = this.previewService.detectKind(name, this.fileBlob.type);
      this.loading = false;
      this.lastResolvedKey = null;
      return;
    }

    if (!this.fileUrl) {
      this.blob = null;
      this.kind = null;
      this.resolvedName = null;
      return;
    }

    const name = this.fileName ?? this.previewService.extractFileName(this.fileUrl);
    this.resolvedName = name;
    const key = this.fileUrl;
    if (this.blob && this.lastResolvedKey === key) {
      this.kind = this.previewService.detectKind(name, this.blob.type);
      return;
    }
    this.loading = true;
    this.kind = null;
    this.blob = null;
    this.fetchSub = this.previewService.fetchBlob(this.fileUrl).subscribe({
      next: blob => {
        this.blob = blob;
        this.kind = this.previewService.detectKind(name, blob.type);
        this.lastResolvedKey = key;
        this.loading = false;
      },
      error: err => {
        this.error = err?.error?.msg || err?.message || 'No se pudo cargar el archivo para previsualizar.';
        this.kind = 'unsupported';
        this.loading = false;
      }
    });
  }

  private cleanupTransient(): void {
    if (this.fileUrl && this.lastResolvedKey === this.fileUrl) {
      this.previewService.release(this.fileUrl);
    }
    this.blob = null;
    this.kind = null;
    this.resolvedName = null;
    this.error = null;
    this.loading = false;
    this.lastResolvedKey = null;
  }
}
